import React, { useState } from 'react'
import { api } from '../../utils/api'
import { postageTaskLabel } from '../../../../shared/collection-postage-tasks.js'
import { previewHandTasks } from './collectionPostageTasks.js'
import PostageTaskItem from './PostageTaskItem.jsx'

const SUB_HEADING_STYLE = { fontSize: 11, fontWeight: 600, letterSpacing: 0.4, color: 'var(--color-text-muted)', margin: '12px 0 4px' }
const PICKUP_DATE_FORMAT = new Intl.DateTimeFormat('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })
const REASON_MAX_LENGTH = 500
const BAD_GATEWAY = 502

/**
 * "Cancel this collection?" (Trac #993): the reason, what Prism does for staff,
 * what staff do by hand, and then the honest answer. `parcels` is the GET
 * parcels answer (or null), `pickup` the FedEx pickup Prism booked (or null).
 */
export default function CollectionCancelSection({ shipment, parcels, pickup, onKeep, onCancelled }) {
  const [reason, setReason] = useState('')
  const [notifyCustomer, setNotifyCustomer] = useState(true)
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState(null)
  const [result, setResult] = useState(null)

  async function cancel() {
    setBusy(true)
    setFailure(null)
    try {
      const answer = await api.post(`/api/deals/collections/${shipment.id}/cancel`, {
        reason: reason.trim(),
        notify_customer: notifyCustomer,
      })
      setResult(answer)
      onCancelled?.(answer)
    } catch (e) {
      setFailure(describeFailure(e))
    } finally {
      setBusy(false)
    }
  }

  if (result) return <CancelledAnswer result={result} />

  const handTasks = previewHandTasks({
    collection: shipment,
    parcels: parcels?.parcels ?? [],
    prismBookedPickup: !!pickup,
  })

  return (
    <section aria-labelledby="cancel-collection-heading" data-testid="cancel-section" style={SECTION_STYLE}>
      <h3 id="cancel-collection-heading" style={{ fontSize: 15, margin: '0 0 8px' }}>Cancel this collection?</h3>

      <div className="form-group" style={{ margin: 0 }}>
        <label htmlFor="cancel-reason" style={{ fontSize: 11, fontWeight: 600 }}>REASON</label>
        <input id="cancel-reason" type="text" className="form-input" value={reason} maxLength={REASON_MAX_LENGTH}
          onChange={e => setReason(e.target.value)} placeholder="Why is it cancelled?" />
      </div>

      <div style={SUB_HEADING_STYLE}>PRISM DOES THIS FOR YOU</div>
      {pickup && (
        <label style={TICK_STYLE}>
          <input type="checkbox" checked disabled readOnly />
          <span>Cancel the pickup with FedEx (booked through Prism for {pickupDateText(pickup.collectionDate)})</span>
        </label>
      )}
      <label style={TICK_STYLE}>
        <input type="checkbox" checked={notifyCustomer} onChange={e => setNotifyCustomer(e.target.checked)} />
        <span>Email the customer that the collection is cancelled</span>
      </label>

      {handTasks.length > 0 && (
        <>
          <div style={SUB_HEADING_STYLE}>YOU DO THIS BY HAND</div>
          {handTasks.map(task => (
            <label key={task.kind} style={TICK_STYLE}>
              <input type="checkbox" checked={false} disabled readOnly />
              <span>{handTaskText(task)}</span>
            </label>
          ))}
          <p style={{ fontSize: 12, color: 'var(--color-text-muted)', margin: '8px 0 0' }}>
            Cancelling the pickup does not void the labels. Until this is ticked, the collection stays in a
            &ldquo;Postage to cancel&rdquo; list on the board.
          </p>
        </>
      )}

      {failure && <p role="alert" style={{ fontSize: 13, color: 'var(--color-error)', margin: '12px 0 0' }}>{failure}</p>}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
        <button type="button" className="btn btn-secondary" onClick={onKeep}>Keep it</button>
        <button type="button" className="btn" style={DANGER_OUTLINE_STYLE} disabled={busy || !reason.trim()} onClick={cancel}>
          {busy ? 'Cancelling' : 'Cancel collection'}
        </button>
      </div>
    </section>
  )
}

function CancelledAnswer({ result }) {
  const [tasks, setTasks] = useState(result.postage_tasks ?? [])
  return (
    <section data-testid="cancel-result" style={SECTION_STYLE}>
      <h3 style={{ fontSize: 15, margin: '0 0 8px' }}>Collection cancelled</h3>
      <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>
        <li>{pickupLine(result)}</li>
        <li>{emailLine(result)}</li>
      </ul>
      {tasks.length > 0 && (
        <>
          <div style={SUB_HEADING_STYLE}>YOU DO THIS BY HAND</div>
          {tasks.map(task => (
            <PostageTaskItem
              key={task.collection_postage_task_id}
              task={task}
              onDone={updated => setTasks(prev => prev.map(t => t.collection_postage_task_id === updated.collection_postage_task_id ? updated : t))}
            />
          ))}
          <p style={{ fontSize: 12, color: 'var(--color-text-muted)', margin: '8px 0 0' }}>
            Until each is done, the collection stays in the &ldquo;Postage to cancel&rdquo; list on the board.
          </p>
        </>
      )}
    </section>
  )
}

const SECTION_STYLE = {
  border: '1px solid var(--color-error)',
  background: 'rgba(229, 98, 94, 0.05)',
  borderRadius: 'var(--radius)',
  padding: 'var(--space-3)',
  marginTop: 'var(--space-4)',
}
const TICK_STYLE = { display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13, padding: '3px 0' }
const DANGER_OUTLINE_STYLE = { background: 'white', color: 'var(--color-error)', border: '1px solid var(--color-error)' }

function pickupDateText(isoDate) {
  const date = new Date(`${isoDate}T00:00:00`)
  return Number.isNaN(date.getTime()) ? isoDate : PICKUP_DATE_FORMAT.format(date)
}

function handTaskText(task) {
  const base = postageTaskLabel(task.kind)
  return task.trackingNumbers.length > 0 ? `${base} (${task.trackingNumbers.join(', ')})` : base
}

function pickupLine(result) {
  if (!result.pickup_cancelled) return 'No FedEx pickup booked through Prism, so none was cancelled'
  const code = result.pickup?.confirmation_code
  return `FedEx pickup cancelled${code ? ` (confirmation ${code})` : ''}`
}

function emailLine(result) {
  if (result.email_sent) return 'Customer emailed'
  if (result.email_reason === 'not_asked') return 'Customer not emailed, as you chose'
  return `Customer was NOT emailed${result.email_reason ? ` (${result.email_reason})` : ''}. Tell them yourself`
}

// Says what the failed cancel left behind, from what the worker reported.
function describeFailure(error) {
  const detail = error.message
  if (error.body?.pickup_cancelled === true) {
    return `The FedEx pickup was cancelled, but the collection was not. ${detail}`
  }
  if (error.status === BAD_GATEWAY || error.body?.cancelled === false) {
    return `Nothing was cancelled. ${detail}`
  }
  return `Could not cancel: ${detail}`
}
